import React, { useMemo } from 'react';

import { Typography, Chip, Tooltip } from '@material-ui/core';
import { Lock as LockIcon, Warning as WarningIcon, Info as InfoIcon, Error as ErrorIcon } from '@material-ui/icons';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import InformationSummary from 'components/InformationSummary';
import RichTextContent from 'components/RichTextContent/RichTextContent';
import type { ActionTemplate } from 'config/api/actionLibrary/actionLibrary.types';
import { translatedOrFallback } from 'helpers/safeT/safeT';
import action_library_messages from 'messages/action_library_messages';

import ActionTemplateDetailsSteps from './_components/ActionTemplateDetailsSteps';
import StepsNavigation from './_components/StepsNavigation/StepsNavigation';
import useStyles from './ActionTemplateDetails.styles';

type Props = {
  template: ActionTemplate;
  actions?: React.ReactNode;
};

const ActionTemplateDetails: React.FC<Props> = ({ template, actions }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const validityPeriod = useMemo(() => {
    if (template.validityPeriodMonths) {
      const unit = template.validityPeriodMonths === 1 ? t(action_library_messages.time.month) : t(action_library_messages.time.months);
      return `${template.validityPeriodMonths} ${unit}`;
    }
    return t(action_library_messages.properties.doesnt_apply);
  }, [template.validityPeriodMonths, t]);

  const informationSummaryData = useMemo(() => {
    const data: Array<{ label: string[]; value: string | number | React.ReactElement | null | undefined }> = [
      {
        label: action_library_messages.table.criticality,
        value: (() => {
          const criticalityKey = template.criticality.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH';
          const displayValue = t(action_library_messages.criticality[criticalityKey]);
          let IconComponent;
          let iconClass;

          if (template.criticality === 'low') {
            IconComponent = InfoIcon;
            iconClass = styles.criticalityLowIcon;
          } else if (template.criticality === 'medium') {
            IconComponent = WarningIcon;
            iconClass = styles.criticalityMediumIcon;
          } else {
            IconComponent = ErrorIcon;
            iconClass = styles.criticalityHighIcon;
          }

          return (
            <div className={styles.valueWithIcon}>
              <IconComponent className={`${styles.statusIcon} ${iconClass}`} />
              <span>{displayValue}</span>
            </div>
          );
        })(),
      },
      {
        label: action_library_messages.table.area,
        value: (template.areaData?.displayName as string) || template.areaDisplay || template.area,
      },
      {
        label: action_library_messages.table.subarea,
        value: (() => {
          if (!template.subAreaData) return template.subArea || '-';
          return translatedOrFallback(t, template.subAreaData.nameTranslationKey, template.subAreaData.name as string);
        })(),
      },
      {
        label: action_library_messages.detail.reported_to,
        value: template.reportedTo || '-',
      },
    ];

    if (template.ownerType === 'organization' && template.ownerOrganization) {
      data.push({
        label: action_library_messages.detail.owner_organization,
        value: template.ownerOrganization,
      });
    }

    // Add created date
    data.push({
      label: action_library_messages.detail.created_at,
      value: format(new Date(template.createdAt), 'yyyy-MM-dd HH:mm'),
    });

    // Add updated date
    if (template.updatedAt) {
      data.push({
        label: action_library_messages.detail.updated_at,
        value: format(new Date(template.updatedAt), 'yyyy-MM-dd HH:mm'),
      });
    }

    // Add created by if available
    if (template.createdBy) {
      data.push({
        label: action_library_messages.detail.created_by,
        value: template.createdBy.fullName,
      });
    }

    return data;
  }, [template, t, styles, validityPeriod]);

  const oneColumnData = useMemo(() => {
    const data: Array<{ label: string[]; value: string | number | React.ReactElement | null | undefined }> = [
      {
        label: action_library_messages.detail.reference,
        value: template.reference ? (
          <div className={styles.tagsContainer}>
            {template.reference.split(', ').map((ref, index) => (
              <Tooltip key={index} title={ref}>
                <Chip key={index} className={clsx(styles.chips, styles.tooltipChipTruncated)} label={ref} size='small' />
              </Tooltip>
            ))}
          </div>
        ) : (
          '-'
        ),
      },
      {
        label: action_library_messages.detail.asset_types,
        value:
          template.assetCategories && template.assetCategories.length > 0 ? (
            <div className={styles.tagsContainer}>
              {template.assetCategories.map(category => (
                <Tooltip key={category.id} title={category.name}>
                  <Chip key={category.id} className={clsx(styles.chips, styles.tooltipChipTruncated)} label={category.name} size='small' />
                </Tooltip>
              ))}
            </div>
          ) : (
            '-'
          ),
      },
      {
        label: action_library_messages.detail.tags,
        value:
          template.tags && typeof template.tags === 'object' && Object.keys(template.tags).length > 0 ? (
            <div className={styles.tagsContainer}>
              {Object.entries(template.tags).map(([key, value]) => (
                <Tooltip key={key} title={`${String(value).toLowerCase()}`}>
                  <Chip
                    key={key}
                    className={clsx(styles.chips, styles.tooltipChipTruncated)}
                    label={`${String(value).toLowerCase()}`}
                    size='small'
                  />
                </Tooltip>
              ))}
            </div>
          ) : (
            '-'
          ),
      },
    ];

    return data;
  }, [template, styles]);

  const validityPeriodData = useMemo(
    () => [
      {
        label: action_library_messages.detail.validity_period,
        value: validityPeriod,
      },
    ],
    [validityPeriod],
  );

  const lockIcon = useMemo(() => {
    if (template.isLocked) {
      return <LockIcon color='error' fontSize='small' />;
    }
    return null;
  }, [template.isLocked]);

  return (
    <>
      <div className={styles.stickyMenuContainer}>
        {actions}
        {template.steps && template.steps.length > 0 && <StepsNavigation steps={template.steps} />}
      </div>
      <CenteredGrid iconLeft={lockIcon} tight title={template.name} width='lg' withoutPadding />
      <CenteredGrid gridGap={2} tight width='lg'>
        <Typography style={{ marginBottom: 8 }} variant='body2'>
          {template.shortDescription ? <RichTextContent content={template.shortDescription} /> : '-'}
        </Typography>
      </CenteredGrid>

      <CenteredGrid gridGap={2} tight title='' width='lg'>
        <InformationSummary data={informationSummaryData} />
      </CenteredGrid>

      <CenteredGrid gridGap={2} tight title='' width='lg'>
        <InformationSummary data={oneColumnData} oneColumn />
      </CenteredGrid>

      <CenteredGrid gridGap={2} tight title='' width='lg'>
        <InformationSummary data={validityPeriodData} oneColumn />
      </CenteredGrid>

      {template.steps && template.steps.length > 0 && (
        <CenteredGrid gridGap={2} id='action-template-steps-section' tight title={t(action_library_messages.detail.steps)} width='lg'>
          <ActionTemplateDetailsSteps steps={template.steps} />
        </CenteredGrid>
      )}
    </>
  );
};

export default ActionTemplateDetails;
